/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'util/PropTypes';
import StringResources from 'nls/StringResources';
import { AutoCompleteInput } from '@ba/ba-ui-carbon-toolkit';
import { SearchInput } from '@ba/ba-ui-carbon-toolkit';
import { ShapingPickTree } from 'ca-modeller/shaping';
import Dropdown, { eventIsInDropdown } from '../dropdownMenu/Dropdown';
import FieldsList from '../fieldsList/FieldsList';

export const FIELD_INPUT_DROPDOWN_MODES = {
	EMPTY: 0,
	INTERESTING_FIELDS: 1,
	METADATA_TREE: 2
};

@observer
class FieldInput extends Component {

	constructor(props) {
		super(props);
		this.props = props;
		this.filteredValues = [];

		//Use state for whether focus is in the control as it's a very transient property
		this.state = {
			hasFocus: false,
			userTextInput: ''
		};


		mobx.reaction(() => {
			return this.props.fields.clearInput;
		}, clearInput => {
			if(clearInput) {
				this.setState({
					userTextInput: ''
				});
				this.filteredValues = [];
			}
		});
	}

	componentDidMount() {
		const glassContext = this.props.glassContext;
		if(glassContext){
			this.props.relationshipVM.setGlassContext(glassContext);
		}

		if(this.props.fields.isNewStartingPointsFeatureEnabled()) {
			this._bodyOnMouseDownHandler = this._bodyOnMouseDown.bind(this);
			document.body.addEventListener('mousedown', this._bodyOnMouseDownHandler);
		}
	}

	componentWillUnmount() {
		if(this._bodyOnMouseDownHandler) {
			window.removeEventListener('mousedown', this._bodyOnMouseDownHandler);
			this._bodyOnMouseDownHandler = null;
		}
	}

	_shouldShowDropdown() {
		if(!this.props.fields.isNewStartingPointsFeatureEnabled()) {
			return false;
		}

		if(!this.state.hasFocus) {
			//Do not render dropdown when not in focus
			return false;
		}

		const renderMode = this.props.getDropdownMode({ userTextInput: this.state.userTextInput });

		if(renderMode === FIELD_INPUT_DROPDOWN_MODES.INTERESTING_FIELDS) {
			return true;
		} else if(renderMode === FIELD_INPUT_DROPDOWN_MODES.METADATA_TREE) {
			return true;
		}

		return false;
	}

	/*
	 * @return the actual text input from which the dropdown appears
	 */
	_renderTextInput(getTargetNode) {
		const clearText = StringResources.get('startingPoints_fieldInputClear');
		return (
			<SearchInput
				className='fieldInput'
				value={this.state.hasFocus ? this.state.userTextInput : this.props.focusField}
				placeHolderText={this.props.placeholder}
				onChange={value => this.onChange(value)}
				onKeyDown={e => this.onKeyDown(e) }
				onFocus={() => this.onFocus()}
				ref= {
					(node) => {
						this._textInputRef = node;
						getTargetNode(node);
					}
				}
				closeButtonLabelText={clearText}
				size='lg'
			/>
		);
	}

	_renderRecentFields() {
		const fields = this.props.recentFields;
		return fields && fields.length ? (
			<FieldsList
				title={StringResources.get('startingPoints_recentlyUsed')}
				fields={fields.map(({ id, name }) => ({ id, label: name }))}
				onSelectField={
					({ id }) => this.performAction({ value: id })
				}
				activeDataSourceModule={this.props.activeDataSourceModule}
				shouldUseDisambiguatedPaths={this.props.shouldUseDisambiguatedPaths}
				relationshipVM = {this.props.relationshipVM}
			/>
		) : null;
	}

	//Replace this with the button list component that support cancel
	_renderRunningButton(){
		const runningRequestsTargetIds = this.props.relationshipVM.getRunningRequestsTargetIds();
		if(runningRequestsTargetIds.length) {
			const allFields = this.props.fields.getFields().getAll();
			const fields = [];
			runningRequestsTargetIds.forEach(targetId => {
				const foundField = allFields.find(field => field.id === targetId);
				if (foundField) {
					fields.push({
						id: targetId,
						label: foundField.name,
						deletable: true
					});
				}
			});
			if (fields.length > 0) {
				return <FieldsList
					title={StringResources.get('startingPoints_runningRelationshipRequest')}
					fields={fields}
					onDeleteField={
						({ id }) => {
							const targetModelToAbandon = this.props.relationshipVM.getTargetModel({ targetId: id });
							targetModelToAbandon && targetModelToAbandon.abandonRequest();
						}
					}
					onSelectField={
						({ id }) => this.performAction({ value: id })
					}
					activeDataSourceModule={this.props.activeDataSourceModule}
					shouldUseDisambiguatedPaths={this.props.shouldUseDisambiguatedPaths}
					relationshipVM = {this.props.relationshipVM}
				/>;
			} else {
				return null;
			}

		} else {
			return null;
		}
	}

	_renderRecommendedFields() {
		const fields = this.props.recommendedFields || [];
		const error = fields.length ? null : {
			title: StringResources.get('startingPoints_recommended_fields_unavailable'),
			description: StringResources.get('startingPoints_wizard_recommendations_enter_column')
		};
		return (
			<FieldsList
				title={StringResources.get('startingPoints_recommendedFieldsListTitle')}
				fields={fields.map(
					({ id, name }) => ({ id, label: name })
				)}
				error={error}
				onSelectField={
					({ id }) => this.performAction({ value: id })
				}
				activeDataSourceModule={this.props.activeDataSourceModule}
				shouldUseDisambiguatedPaths={this.props.shouldUseDisambiguatedPaths}
				relationshipVM = {this.props.relationshipVM}
			/>
		);
	}

	_renderDropdown() {
		const renderMode = this.props.getDropdownMode({ userTextInput: this.state.userTextInput });

		if(renderMode === FIELD_INPUT_DROPDOWN_MODES.INTERESTING_FIELDS) {
			return [
				this._renderRecentFields(),
				this.props.relationshipVM && this.props.relationshipVM.limitConcurrentRequests() ? this._renderRunningButton() : null,
				this._renderRecommendedFields()
			];
		} else if(renderMode === FIELD_INPUT_DROPDOWN_MODES.METADATA_TREE) {
			const module = this.props.module;
			const shapingHelper = module.getShapingHelper();
			return (
				<shapingHelper.ShapingProvider>
					<ShapingPickTree
						filter={this.state.userTextInput}
						onSelect={item => this.performAction({ value: item.idForExpression })}
					/>
				</shapingHelper.ShapingProvider>
			);
		}
	}

	render() {
		return (
			this.props.fields.isNewStartingPointsFeatureEnabled() ?
				(
					<Dropdown
						renderTrigger={(getTargetNode)=>this._renderTextInput(getTargetNode)}
						renderDropdownContents={()=>this._renderDropdown()}
						open={this._shouldShowDropdown()}
						getDropdownMaxHeight={this.props.getDropdownMaxHeight}
						onBlur={() => this.onBlur()}
						animationWhenContentChanges={false} // Need to set this prop to `false` to avoid the dropdown keep boucing when user types in the field input
					/>
				) : (
					<div className="field-input">
						<AutoCompleteInput
							values={this.props.values}
							value={this.state.userTextInput}
							placeholder={StringResources.get('startingPoints_fieldInputPlaceHolder')}
							onChange={value => this.onChange(value)}
							onSelectionChange={value => this.onSelectionChange(value)}
							onKeyDown={e => this.onKeyDown(e) }
							onValueMatch={filteredValues => this.onValueMatch(filteredValues)}
						/>
					</div>
				)
		);
	}


	_renderError(error) {
		return (
			<div className='fieldsListContainer'>
				<div className='fieldsListTitle'>{error.title}</div>
				<div className='fieldsListErrorMessage'>{error.description}</div>
			</div>
		);
	}

	onKeyDown(e) {
		const handler = {
			'Enter': () => {
				const value = this._getTypedValue();
				if(value) {
					this.performAction(value);
				}
			}
		}[e.key];

		if(handler) {
			handler();
		}
	}

	_bodyOnMouseDown(evt) {
		if(!eventIsInDropdown(evt)) {
			this.onBlur();
		}
	}

	onFocus() {
		this.setState({
			hasFocus: true
		});
	}

	onBlur() {
		this.setState({
			hasFocus: false,
			userTextInput: ''
		});
	}

	/*
	 * @return the value associated with the label typed by the user, null if the
	 * user's entry does not resolve meaningfully to a suggested value
	 */
	_getTypedValue() {
		if(this.filteredValues.length === 0) {
			//No suggested values. No meaningful value.
			return null;
		} else if(this.filteredValues.length === 1) {
			//Exactly one suggested value. User must have wanted that value.
			return this.filteredValues[0];
		}
	}

	onChange(value) {
		this.setState({
			userTextInput: value
		});
		this.props.fields.setClearInput(false);
	}

	onSelectionChange(value) {
		this.performAction(value);
	}

	onValueMatch(values) {
		this.filteredValues = values;
	}

	performAction(selectedValue) {
		if (this.props.onChange) {
			this.props.onChange(selectedValue.value);
			this.setState({
				userTextInput: ''
			});
			this.filteredValues = [];
		}
		this.setState({
			hasFocus: false
		});
	}
}

const FieldProp = PropTypes.shape({
	id: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired
});

const FieldArrayProp = PropTypes.observableArrayOf(FieldProp);


FieldInput.propTypes = {
	values: PropTypes.arrayOf(PropTypes.object),	/*deprecated - old UI*/
	fields: PropTypes.object.isRequired,
	onChange: PropTypes.func,
	recentFields: FieldArrayProp,
	recommendedFields: FieldArrayProp,
	focusField: PropTypes.string,
	placeholder: PropTypes.string,
	module: PropTypes.observableObject,
	getDropdownMode: PropTypes.func.isRequired,
	getDropdownMaxHeight: PropTypes.func.isRequired,
	activeDataSourceModule: PropTypes.observableObject,
	shouldUseDisambiguatedPaths: PropTypes.bool,
	relationshipVM: PropTypes.object.isRequired,
	glassContext: PropTypes.object.isRequired
};

FieldInput.defaultProps = {
	focusField: '',
	placeholder: ''
};

export default FieldInput;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/FieldInput.js